import React, { ReactNode, useState } from "react";
import {
  Grant,
  Role,
  UserDataFragment,
  useRolesQuery,
  useSwitchRoleMutation
} from "generated/graphql";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { RoleProps } from "../../Profile/ProfileView";
import { DropdownList, PolyIcon } from "elements";
import { RoleName, RoleNames } from "roles";
import { useAuth0 } from "@auth0/auth0-react";
import { TABLES } from "store/userMeta";
import { infiniteNestLink, Link } from "polyrouter";
import { handleNullFields } from "utilities/stringUtils";
import { TableBulkItemRow } from "collections/Table";
import { DISMISS, useNotification } from "collections";

export function getRoleFromGrants(
  tenantId: string,
  grants?: Grant[]
): RoleName {
  const grant = grants?.find(grant => grant.resourceId === tenantId);
  if (grant) {
    const roles = grant.roles.map(role => role.name);
    for (const role of roles) {
      if (RoleNames.includes(role)) {
        return role as RoleName;
      }
    }
  }
  return "NONE";
}

export type UserRowProps = UserDataFragment & { role: RoleName };

export type UserDetailProps = UserRowProps & {
  cursor: string;
};

export type GrantProps = Pick<Grant, "resourceId" | "resourceType"> & {
  roles: Array<Pick<Role, "name" | "tags">>;
};

export const RoleColumn: React.FC<{
  userId: string;
  role: RoleName;
  resourceId: string;
  tags: string[];
  canEdit: boolean;
  "data-testid"?: string;
}> = ({ userId, resourceId, role: roleInit, tags, canEdit, ...rest }) => {
  const { t } = useTranslation();
  const [, switchRole] = useSwitchRoleMutation();
  const [role, setRole] = useState<RoleName>(roleInit);
  const [addNotification] = useNotification();

  const { data: roleData } = useRolesQuery({ variables: { tags } });
  const roleProps = (roleData?.roles ?? []) as Array<RoleProps>;
  const roles = roleProps.map(({ name }) => name);

  const onChange = (value: string) => {
    switchRole({
      accessorId: userId,
      resourceId,
      resourceType: "Tenant",
      role: value,
      tags
    }).then(response => {
      const newRole = response.data?.switchRole.grants
        .filter(grant => grant.resourceId === resourceId)[0]
        .roles.filter(role => role.tags.includes(tags![0]))[0]?.name;
      if (newRole) setRole(newRole as RoleName);
      if (response.error) {
        addNotification({
          id: "switch-user-role",
          type: "error",
          message: t("ROLE_SWITCH_ERROR"),
          dismiss: DISMISS.DEFAULT
        });
      }
    });
  };

  if (!canEdit) return <>{t(role as LocalKeys)}</>;

  return (
    <DropdownList
      name={t("ROLE")}
      options={roles.map(role => ({
        text: t(role as LocalKeys),
        value: role
      }))}
      onChange={onChange}
      value={role ?? t("UNKNOWN")}
      testid={rest["data-testid"]}
    />
  );
};

export const UserRow: React.FC<{
  tags: string[];
  user: UserRowProps;
  checked: boolean;
  onCheck: () => void;
  canEdit: boolean;
  showChecks: boolean;
  index: number;
  resourceId: string;
  shownColumns: Array<string>;
}> = ({
  tags,
  user,
  checked,
  onCheck,
  canEdit = false,
  showChecks = false,
  index,
  resourceId,
  shownColumns
}) => {
  const { user: authUser } = useAuth0();
  const currentUserId = authUser ? authUser.sub : "";
  const isNotCurrentUser = user.user_id !== currentUserId;
  const { COLUMNS } = TABLES.MEMBERS;

  const getTableContent = (column: string) => {
    let testId: string | undefined = undefined;
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.NAME:
        testId = `user-order-idx-${index}`;
        content = (
          <Link to={infiniteNestLink(`/details/${user.user_id}`)}>
            {handleNullFields(user.name)}
          </Link>
        );
        break;
      case COLUMNS.EMAIL:
      case COLUMNS.LOGINS_COUNT:
        const prop = column as "email" | "logins_count";
        content = user[prop];
        break;
      case COLUMNS.CREATED_AT:
        content = new Date(
          Date.parse(user.created_at ?? "")
        ).toLocaleDateString();
        break;
      case COLUMNS.ROLE:
        testId = `role-for-user-${index}`;
        content = (
          <RoleColumn
            userId={user.user_id}
            canEdit={canEdit && isNotCurrentUser}
            role={user.role}
            resourceId={resourceId}
            tags={tags}
            data-testid={`role-for-user-${index}`}
          />
        );
        break;
    }

    return (
      <td data-testid={testId} key={column}>
        {content}
      </td>
    );
  };

  return (
    <TableBulkItemRow
      showChecks={showChecks}
      checked={checked}
      {...(isNotCurrentUser && canEdit
        ? {
            onCheck,
            replaceCheckWith: undefined
          }
        : {
            onCheck: () => {},
            replaceCheckWith: (
              <PolyIcon icon="person_outline" className="replace-check" />
            )
          })}
    >
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};
